import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import UploadDropZone from './UploadDropZone';
import UploadFileItem from './UploadFileItem';
import EvaluatedFileItem from './EvaluateFileItem';
import EvaluatingSkeletonItem from './EvaluatingSkeletalItem';
import UploadModalFooter from './UploadModalFooter';
import EmptyUploadState from './EmptyUploadState';
import {
  UploadedFile,
  EvaluateApiResponse,
  EvaluationResult,
  LicenseApiFile,
  TechLandscapeUpdate,
  FileStatus,
} from './uploaderTypes';
import SidePanelModal from './SidePanelModal';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import { validateFile } from '../../../common/utils';
import { uploadFile, evaluateFiles, deleteLicenseFile } from './LicenseService';
import { v4 as uuid } from 'uuid';

// ─────────────────────────────────────────────────────────────────────────────
//  Map a LicenseApiFile (from getLicenseDetails) → UploadedFile
//  These are pre-existing files — already uploaded, not yet re-evaluated.
// ─────────────────────────────────────────────────────────────────────────────
function mapLicenseApiFileToUploadedFile(apiFile: LicenseApiFile, resultId: string): UploadedFile {
  // is_valid drives the displayed status — pre-loaded files are already evaluated
  const status: FileStatus =
    apiFile.is_valid === true ? 'Success' : apiFile.is_valid === false ? 'Failed' : 'idle'; // fallback when is_valid is absent

  return {
    id: apiFile.file_id, // CRITICAL: Use backend file_id as the primary identifier
    file: null as unknown as File,
    name: apiFile.file_name,
    isUploading: false,
    fileUrl: apiFile.file_url,
    filePath: apiFile.file_path,
    status,
    summary: apiFile.description,
    fileData: apiFile.file_content,
    isValid: apiFile.is_valid,
    savedOnServer: true, // came from DB
    serverResultId: resultId, // top-level result.id, needed for delete API
    backendFileId: apiFile.file_id, // Store backend ID separately for tracking
  };
}

// ─────────────────────────────────────────────────────────────────────────────
//  Maps the raw EvaluateApiResponse array → EvaluationResult[]
// ─────────────────────────────────────────────────────────────────────────────
function mapEvaluateResponses(apiResponses: EvaluateApiResponse[]): EvaluationResult[] {
  return apiResponses.map((resp) => ({
    id: resp?.id,
    status: resp.is_valid ? 'Success' : 'Failed',
    summary: resp.one_line_summary,
    invalidReason: !resp.is_valid ? resp.one_line_summary : undefined,
    evaluationId: resp.id,
    isValid: resp.is_valid,
    validationStatus: resp.validation_status,
    confidenceScore: resp.confidence_score,
    detectedFunction: resp.detected_function,
    fileData: resp.fileData,
    evaluatedFileUrl: resp.fileUrl,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────

type ModalPhase = 'idle' | 'evaluating' | 'evaluated';

interface EnterpriseUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when the user clicks "Update Tech Landscape" */
  onUpdate: (data: TechLandscapeUpdate) => Promise<void>;
  /** Pre-existing files from getLicenseDetails API */
  preLoadedFiles?: LicenseApiFile[];
  /** When true, skip straight to evaluated screen on open */
  openInManageMode?: boolean;
  /**
   * Top-level result.id from getLicenseDetails.
   * Required to call deleteLicenseFile(serverResultId, fileId).
   */
  serverResultId?: string;
  getUploadedLicenseDetails: () => void;
  updatingTechLandscape: boolean;
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
}

const EnterpriseUploadModal: React.FC<EnterpriseUploadModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  preLoadedFiles = [],
  openInManageMode = false,
  serverResultId = '',
  getUploadedLicenseDetails = () => {},
  updatingTechLandscape = false,
  files,
  setFiles,
}) => {
  const [phase, setPhase] = useState<ModalPhase>('idle');
  const [evaluatedSnapshot, setEvaluatedSnapshot] = useState<string[]>([]);
  // Track which backend file IDs we've already loaded to prevent duplicates
  const [loadedBackendFileIds, setLoadedBackendFileIds] = useState<Set<string>>(new Set());

  // ── Seed state from pre-loaded files when modal opens ────────────────────
  useEffect(() => {
    if (!isOpen) {
      // Reset tracking when modal closes
      setLoadedBackendFileIds(new Set());
      return;
    }

    if (preLoadedFiles.length === 0) return;

    const mapped = preLoadedFiles.map((f) => mapLicenseApiFileToUploadedFile(f, serverResultId));
    const newBackendIds = new Set(preLoadedFiles.map((f) => f.file_id));

    setFiles((prev) => {
      // Separate files into categories
      const localFiles = prev.filter((f) => !f.savedOnServer);
      const serverFiles = prev.filter((f) => f.savedOnServer);

      // Create a map of existing server files by backend ID
      const existingServerMap = new Map(serverFiles.map((f) => [f.backendFileId!, f]));

      // Process each mapped file
      const processedServerFiles = mapped.map((newFile) => {
        const existingFile = existingServerMap.get(newFile.backendFileId!);

        if (existingFile) {
          // File exists - merge/update it
          return {
            ...existingFile,
            // Update fields that might have changed from the backend
            summary: newFile.summary,
            isValid: newFile.isValid,
            status: newFile.status,
            fileData: newFile.fileData,
            fileUrl: newFile.fileUrl,
            filePath: newFile.filePath,
          };
        } else {
          // New file - add it
          return newFile;
        }
      });

      // Update loaded tracking
      setLoadedBackendFileIds(new Set(newBackendIds));

      // Combine: processed server files + local files
      return [...processedServerFiles, ...localFiles];
    });

    // Only set phase on initial modal open, not on subsequent updates
    if (phase === 'idle' || !loadedBackendFileIds.size) {
      if (openInManageMode) {
        setPhase('evaluated');
        setEvaluatedSnapshot(preLoadedFiles.map((f) => f.file_id));
      } else {
        const allHaveStatus = preLoadedFiles.every((f) => f.is_valid !== undefined);
        if (allHaveStatus) {
          setPhase('evaluated');
          setEvaluatedSnapshot(preLoadedFiles.map((f) => f.file_id));
        }
      }
    }
  }, [isOpen, preLoadedFiles, openInManageMode, serverResultId]);

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleFilesAdded = useCallback(
    async (rawFiles: File[]) => {
      const validatedFiles: { file: File }[] = [];

      const ALLOWED_TYPES = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      const ALLOWED_EXTENSIONS = ['.pdf', '.docx'];

      for (const file of rawFiles) {
        const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
        const isAllowedType =
          ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(fileExtension);

        if (!isAllowedType) {
          ShowToast({
            message: `${file.name}: Only PDF and DOCX files are allowed.`,
            type: ToastTypeEnum.Error,
          });
          continue;
        }

        const isDuplicate = files.some((f) => f.name === file.name);
        if (isDuplicate) continue;

        const validation = await validateFile(file);
        if (!validation.ok) {
          ShowToast({
            message: `${file.name}: ${validation.error}`,
            type: ToastTypeEnum.Error,
          });
        } else {
          validatedFiles.push({ file });
        }
      }

      if (validatedFiles.length === 0) return;

      const tempFiles: UploadedFile[] = validatedFiles.map(({ file }) => ({
        id: uuid(),
        name: file.name,
        file,
        isUploading: true,
        status: 'idle',
        savedOnServer: false, // New uploads are not saved on server yet
      }));

      setFiles((prev) => [...prev, ...tempFiles]);
      setPhase((prev) => (prev === 'evaluated' ? 'idle' : prev));

      for (const tempFile of tempFiles) {
        try {
          const response = await uploadFile(tempFile.file);

          if (response?.files?.length > 0) {
            const fileUrl: string = response?.files?.[0];
            setFiles((prev) =>
              prev.map((f) =>
                f.id === tempFile.id
                  ? {
                      ...f,
                      fileUrl,
                      filePath: fileUrl,
                      fileSize: `${Math.round(tempFile.file.size / 1024)}KB`,
                      isUploading: false,
                    }
                  : f
              )
            );
          } else {
            setFiles((prev) => prev.filter((f) => f.id !== tempFile.id));
            ShowToast({
              message: `Upload failed for ${tempFile.name}`,
              type: ToastTypeEnum.Error,
            });
          }
        } catch (error) {
          setFiles((prev) => prev.filter((f) => f.id !== tempFile.id));
          ShowToast({
            message: `Error uploading ${tempFile.name}`,
            type: ToastTypeEnum.Error,
          });
          console.error('Error uploading file:', tempFile.name, error);
        }
      }
    },
    [files]
  );

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(
    async (id: string) => {
      const file = files.find((f) => f.id === id);
      if (!file) return;

      if (file.savedOnServer && file.serverResultId) {
        // Server file — call delete API; only remove locally on success
        try {
          const response = await deleteLicenseFile(file.serverResultId, file.id);
          if (response) {
            // Remove from loaded tracking
            if (file.backendFileId) {
              setLoadedBackendFileIds((prev) => {
                const updated = new Set(prev);
                updated.delete(file.backendFileId!);
                return updated;
              });
            }

            await getUploadedLicenseDetails();
            setFiles((prev) => {
              const next = prev.filter((f) => f.id !== id);
              if (next.length === 0) setPhase('idle');
              return next;
            });
          }
        } catch (error) {
          console.error('Failed to delete file from server:', error);
          ShowToast({ message: `Failed to delete ${file.name}`, type: ToastTypeEnum.Error });
          return; // don't remove locally if server delete failed
        }
      } else {
        // Local-only file → remove from state
        setFiles((prev) => {
          const next = prev.filter((f) => f.id !== id);
          if (next.length === 0) setPhase('idle');
          return next;
        });
      }
    },
    [files, getUploadedLicenseDetails]
  );

  // ── Evaluate ──────────────────────────────────────────────────────────────
  const handleEvaluate = useCallback(async () => {
    const readyFiles = files.filter((f) => !f.isUploading && f.filePath);
    if (!readyFiles.length) return;

    setPhase('evaluating');
    setFiles((prev) => prev.map((f) => (f.isUploading ? f : { ...f, status: 'evaluating' })));

    try {
      const apiResponses: EvaluateApiResponse[] = await evaluateFiles(readyFiles);
      const results = mapEvaluateResponses(apiResponses);

      setFiles((prev) =>
        prev.map((f) => {
          if (f.isUploading) return f;

          // CRITICAL FIX: Match by file name or path instead of ID
          // because evaluation response may not have the same ID structure
          const result = results.find((r) => {
            // Try to match by evaluationId if it exists in the response
            if (r.evaluationId === f.id) return true;
            // Fallback: match by file properties
            return r.evaluatedFileUrl === f.fileUrl || r.fileData === f.fileData;
          });

          if (!result) return { ...f, status: 'invalid', invalidReason: 'Evaluation failed.' };

          return {
            ...f,
            // CRITICAL: Keep the original ID (backend file_id for server files, uuid for new files)
            // Don't override with evaluation ID
            status: result?.status,
            summary: result?.summary,
            invalidReason: result?.invalidReason,
            evaluationId: result?.evaluationId, // Store evaluation ID separately
            isValid: result?.isValid,
            validationStatus: result?.validationStatus,
            fileData: result?.fileData,
          };
        })
      );
      setPhase('evaluated');
      setEvaluatedSnapshot(readyFiles.map((f) => f.id));
    } catch (error) {
      console.error('Evaluation error:', error);
      setFiles((prev) =>
        prev.map((f) =>
          f.isUploading
            ? f
            : { ...f, status: 'invalid', invalidReason: 'An error occurred during evaluation.' }
        )
      );
      setPhase('evaluated');
      setEvaluatedSnapshot(readyFiles.map((f) => f.id));
    }
  }, [files]);

  // ── Update + Close ────────────────────────────────────────────────────────
  const handleUpdate = useCallback(() => {
    onUpdate({ files });
  }, [files, onUpdate]);

  const handleClose = useCallback(() => {
    if (phase === 'evaluating') return;
    setFiles([]);
    setPhase('idle');
    setEvaluatedSnapshot([]);
    setLoadedBackendFileIds(new Set()); // Reset tracking on close
    onClose();
  }, [onClose, phase]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const hasUploadedFiles = files.some((f) => !f.isUploading && f.filePath);

  const allValid =
    phase === 'evaluated' &&
    files.filter((f) => !f.isUploading).every((f) => f.status === 'Success');

  const currentUploadedIds = files.filter((f) => !f.isUploading && f.filePath).map((f) => f.id);
  const hasChangedSinceEvaluation =
    phase === 'evaluated' &&
    (currentUploadedIds.length !== evaluatedSnapshot.length ||
      currentUploadedIds.some((id) => !evaluatedSnapshot.includes(id)));

  // ── File row renderer ─────────────────────────────────────────────────────
  const renderFileRow = (file: UploadedFile) => {
    if (file.isUploading) {
      return <UploadFileItem key={file.id} file={file} onDelete={handleDelete} />;
    }
    if (file.status === 'evaluating') {
      return <EvaluatingSkeletonItem key={file.id} fileName={file.name} />;
    }
    if (file.status === 'Success' || file.status === 'Failed') {
      return <EvaluatedFileItem key={file.id} file={file} onDelete={handleDelete} />;
    }
    // idle — includes both freshly uploaded and pre-loaded files
    return <UploadFileItem key={file.id} file={file} onDelete={handleDelete} />;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <SidePanelModal isOpen={isOpen} onClose={handleClose} title="Enterprise License Intelligence">
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 3,
            pt: 2.5,
            pb: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: 14,
              color: '#2C2E50',
            }}
          >
            Upload documents/licenses
          </Typography>

          <UploadDropZone onFilesAdded={handleFilesAdded} disabled={phase === 'evaluating'} />

          {files.length === 0 ? (
            <EmptyUploadState />
          ) : (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  fontSize: 14,
                  color: '#2C2E50',
                  mb: 1,
                }}
              >
                Uploaded Files
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {files.map(renderFileRow)}
              </Box>
            </Box>
          )}
        </Box>

        {(hasUploadedFiles || phase === 'evaluated') && (
          <UploadModalFooter
            phase={phase}
            hasFiles={hasUploadedFiles}
            allValid={allValid}
            hasChangedSinceEvaluation={hasChangedSinceEvaluation}
            onEvaluate={handleEvaluate}
            onReEvaluate={handleEvaluate}
            onUpdate={handleUpdate}
            updatingTechLandscape={updatingTechLandscape}
          />
        )}
      </Box>
    </SidePanelModal>
  );
};

export default EnterpriseUploadModal;
